import { ApiResponse } from './api';

/**
 * Treasury — a company investing its own money, built on the ratbi+ plumbing (one group, one department and one
 * employee — the company owner). Mirrors madkhol-thriftplan's TreasuryCompanyResponseDto.
 */
export enum TreasuryCompanyStatus {
  Pending = 'PENDING',
  Active = 'ACTIVE',
}

export interface TreasuryCompanyGroup {
  id: number;
  name: string;
  contribution: number;
  isDeleted: boolean;
  portfolioGroups: { id: number; external_portfolio_id: number }[];
}

export interface TreasuryCompanyDepartment {
  id: number;
  name: string;
  description: string | null;
  isDeleted: boolean;
}

export interface TreasuryCompanyEmployee {
  id: number;
  full_name_en: string;
  full_name_ar: string;
  email: string;
  phone_number: string;
  national_id: string;
  external_user_id: number | null;
  status: string | null;
  is_deleted: boolean;
}

export interface TreasuryCompanyAgreements {
  acceptedAt: string;
  acceptedByType: 'COMPANY_ADMIN' | 'MADKHOL_ADMIN';
  acceptedByName: string | null;
}

export interface TreasuryCompanySetup {
  id: number;
  companyId: number;
  status: TreasuryCompanyStatus;
  companyBankId: number;
  groupId: number;
  departmentId: number;
  /** null until the treasury employee is onboarded. */
  employeeId: number | null;
  group: TreasuryCompanyGroup;
  department: TreasuryCompanyDepartment;
  employee: TreasuryCompanyEmployee | null;
  agreements: TreasuryCompanyAgreements | null;
  reinvestDividend: boolean;
}

export type FetchTreasurySetupResponse = ApiResponse<TreasuryCompanySetup>;

/** Step 1 — the one setup call: the treasury group (exactly one product) and department. */
export interface ApproveCompanyForTreasuryRequest {
  group: {
    name: string;
    contribution: number;
    externalPortfolioIds: number[];
  };
  department: {
    name: string;
    description?: string;
  };
}

export type ApproveCompanyForTreasuryResponse = ApiResponse<unknown>;

/**
 * Step 2 — the treasury employee (the company owner). The ratbi+ employee body without the group and department
 * (thriftplan takes them from the setup), plus the owner's funding sources for the Alpaca KYB.
 */
export type CreateTreasuryEmployeeRequest = Record<string, unknown> & {
  funding_source: string[];
};

export type CreateTreasuryEmployeeResponse = ApiResponse<unknown>;

/** Step 3 — the owner's personal email (replaces the user's email only while it still equals the work email). */
export interface UpdateTreasuryPersonalEmailRequest {
  personalEmail: string;
}

/** madkhol-user's answer, passed through: `needsUserUpdate` is true only when the account email really changed. */
export type UpdateTreasuryPersonalEmailResponse = ApiResponse<{
  needsUserUpdate?: boolean;
  newEmail?: string;
  employeeEmail?: string;
  message?: string;
}>;

/** Step 4 — copies the onboarding KYC to the user, activates the employee and makes the setup ACTIVE. */
export type ApproveTreasuryEmployeeResponse = ApiResponse<unknown>;

/**
 * Step 5 — the owner's KYC confirmation (madkhol-user `kyc.confirm_date`). Approve copies the KYC but leaves the date
 * empty, and the owner's user can't turn Active until it is set.
 */
export interface TreasuryKycConfirmation {
  /** When the KYC was confirmed; null while it isn't. */
  confirmDate: string | null;
  /** True when it was already confirmed before this call (Nafath, the app, or an earlier press). */
  alreadyConfirmed: boolean;
}

export type FetchTreasuryKycConfirmationResponse = ApiResponse<TreasuryKycConfirmation>;

export type ConfirmTreasuryKycResponse = ApiResponse<TreasuryKycConfirmation>;

/** The owner (UBO) and signer — the treasury employee. One 100% owner is supported. */
export interface TreasuryKybUbo {
  percentageOwnership: number;
  titleAtCompany: string;
  countryOfBirth: string;
}

/** Step 5 — the company KYB answers (mirrors madkhol-admin SaveTreasuryCompanyKybDto). */
export interface SaveTreasuryKybRequest {
  legalEntityType: string;
  dateOfIncorporation: string;
  industry: string;
  fundingSource: string[];
  countryOfIncorporation: string;
  countryOfTaxResidence: string;
  annualIncomeMin: number;
  annualIncomeMax: number;
  liquidNetWorthMin: number;
  liquidNetWorthMax: number;
  totalNetWorthMin: number;
  totalNetWorthMax: number;
  isParticipatingFatca: boolean;
  isExemptFromTaxUnder501a: boolean;
  ownerPartyEmail: string;
  entityPartyEmail: string;
  ubo: TreasuryKybUbo;
}

/** The saved KYB, as thriftplan's CompanyKybResponseDto returns it. */
export interface TreasuryKyb extends Omit<SaveTreasuryKybRequest, 'ownerPartyEmail' | 'entityPartyEmail' | 'ubo'> {
  id: number;
  companyId: number;
  ownerPartyEmail: string | null;
  entityPartyEmail: string | null;
  /** Set once it has gone to Alpaca — after that it can't be saved again (KYB_ALREADY_SUBMITTED). */
  submittedAt: string | null;
  ubo: (TreasuryKybUbo & { id: number; employeeId: number | null }) | null;
  verification: {
    verifiedByName: string;
    verifiedAt: string;
    ownerPhotoMatched: boolean;
    ownerDocumentChecked: boolean;
  } | null;
  agreements: TreasuryCompanyAgreements | null;
}

/** GET answers data: null when nothing is saved yet. */
export type FetchTreasuryKybResponse = ApiResponse<TreasuryKyb | null>;

export type SaveTreasuryKybResponse = ApiResponse<TreasuryKyb>;

/** Step 6 — the Madkhol admin confirms the company was checked by hand (mirrors madkhol-admin VerifyTreasuryCompanyKybDto). */
export interface VerifyTreasuryKybRequest {
  /** Required and must be true — Alpaca refuses the owner without the DOCUMENT check. */
  ownerDocumentChecked: boolean;
  ownerPhotoMatched?: boolean;
}

export type VerifyTreasuryKybResponse = ApiResponse<TreasuryKyb>;

/** The owner's account opening agreement signature in madkhol-user (null until signed). */
export interface TreasuryOwnerAgreementSignature {
  id: number;
  createdAt: string;
  updatedAt: string;
}

export type FetchTreasuryOwnerAgreementResponse = ApiResponse<TreasuryOwnerAgreementSignature | null>;

export type SignTreasuryOwnerAgreementResponse = ApiResponse<unknown>;

/** Step 7 — accepting the two Alpaca agreements answers the KYB with its `agreements` set. */
export type AcceptTreasuryAgreementsResponse = ApiResponse<TreasuryKyb>;

/** What is still missing before the company Alpaca account can open (thriftplan KybOnboardingReadiness). */
export interface TreasuryKybReadiness {
  ready: boolean;
  missing: string[];
}

export type FetchTreasuryKybReadinessResponse = ApiResponse<TreasuryKybReadiness>;

/** Step 8 — the company's Alpaca account row (null until opened). */
export interface TreasuryAlpacaAccount {
  accountId: string;
  status: string;
}

/** The newest attempt at opening the account, as madkhol-alpaca reports it (thriftplan CompanyAlpacaOnboardingAttempt). */
export interface TreasuryAlpacaOnboardingAttempt {
  attempt: number;
  /** IN_PROGRESS / DONE / FAILED */
  status: string;
  lastError: string | null;
  accountId: string | null;
  /** What Alpaca last said about the account: SUBMITTED, APPROVED, ACTIVE, REJECTED, ACTION_REQUIRED, … */
  accountStatus: string | null;
  accountStatusReason: string | null;
  accountStatusAt: string | null;
  kycResults: Record<string, unknown> | null;
  lockKyb: boolean;
  updatedAt: string;
}

export interface TreasuryAlpacaOnboarding {
  account: TreasuryAlpacaAccount | null;
  latestAttempt: TreasuryAlpacaOnboardingAttempt | null;
}

export type FetchTreasuryAlpacaOnboardingResponse = ApiResponse<TreasuryAlpacaOnboarding>;

export type RetryTreasuryAlpacaOnboardingResponse = ApiResponse<{
  resubmit: boolean;
  account: TreasuryAlpacaAccount | null;
}>;

export type RetryTreasuryWorldCheckResponse = ApiResponse<{ screenResult: boolean; readiness: TreasuryKybReadiness }>;

/** Props the Create Employee form takes when it is embedded as the treasury employee step. */
export interface TreasuryEmployeeFormMode {
  companyId: number;
  groupName: string;
  departmentName: string;
  onDone: () => void;
}

/** Paging the thriftplan treasury lists read (page is 1-based). */
export interface TreasuryListQuery {
  page: number;
  limit: number;
  status?: string;
}

/** A treasury contribution (thriftplan CompanyContributionResponseDto) — 100% company money. */
export interface TreasuryContribution {
  id: number;
  companyId: number;
  /** pending / in_progress / done / partial_done */
  status: string;
  totalAmountToPay: number;
  employeesAmount: number;
  employeePercentage: number;
  companyAmount: number;
  companyPercentage: number;
  createdAt: string;
  updatedAt: string;
}

export type FetchTreasuryContributionsResponse = ApiResponse<{
  contributions: TreasuryContribution[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}>;

export type CreateTreasuryContributionResponse = ApiResponse<TreasuryContribution>;

export type ExecuteTreasuryContributionResponse = ApiResponse<{ contributionId: number; message: string }>;

export type TreasuryRedemptionType = 'PARTIAL' | 'FULL';

/** Mirrors madkhol-admin CreateTreasuryRedemptionDto — no employee id, thriftplan takes the treasury employee. */
export interface CreateTreasuryRedemptionRequest {
  externalSubscribedPortfolioId: number;
  type: TreasuryRedemptionType;
  /** PARTIAL only. */
  amount?: number;
  reason?: string;
}

/** The redeem request thriftplan created (approved at creation). */
export type CreateTreasuryRedemptionResponse = ApiResponse<{ id: number; status?: string; amount?: number }>;

export type TreasuryRedeemFrequency = 'MONTHLY' | 'QUARTERLY' | 'YEARLY';

export type TreasuryInsufficientFundsAction = 'REDEEM_AVAILABLE' | 'SKIP_AND_NOTIFY';

/** A redemption rule (thriftplan treasury_redeem_automation row). */
export interface TreasuryRedeemAutomation {
  id: number;
  companyId: number;
  employeeId: number;
  externalSubscribedPortfolioId: number;
  amount: number;
  frequency: TreasuryRedeemFrequency;
  dayOfMonth: number;
  monthOfYear: number | null;
  insufficientFundsAction: TreasuryInsufficientFundsAction;
  isActive: boolean;
  lastRunDate: string | null;
  nextRunAt: string;
  retry: number;
  lastError: string | null;
  needManualReview: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Mirrors madkhol-admin CreateTreasuryRedeemAutomationDto. */
export interface SaveTreasuryRedeemAutomationRequest {
  externalSubscribedPortfolioId: number;
  amount: number;
  frequency: TreasuryRedeemFrequency;
  dayOfMonth: number;
  monthOfYear?: number | null;
  insufficientFundsAction: TreasuryInsufficientFundsAction;
}

/** Editing can't change the portfolio (delete and create a new rule instead). */
export type UpdateTreasuryRedeemAutomationRequest = Omit<
  SaveTreasuryRedeemAutomationRequest,
  'externalSubscribedPortfolioId'
>;

/** One firing of a rule (thriftplan treasury_redeem_automation_run row). */
export interface TreasuryRedeemAutomationRun {
  id: number;
  automationId: number;
  scheduledFor: string;
  /** SUCCESS / FAILED / SKIPPED_NO_FUNDS / SKIPPED_NOT_REDEEMABLE */
  status: string;
  requestedAmount: number;
  requestType: TreasuryRedemptionType;
  contributionRedeemRequestId: number | null;
  errorMessage: string | null;
  attempt: number;
  createdAt: string;
}

export type FetchTreasuryRedeemAutomationsResponse = ApiResponse<TreasuryRedeemAutomation[]>;

export type SaveTreasuryRedeemAutomationResponse = ApiResponse<TreasuryRedeemAutomation>;

export type FetchTreasuryRedeemAutomationRunsResponse = ApiResponse<TreasuryRedeemAutomationRun[]>;

export type DeleteTreasuryRedeemAutomationResponse = ApiResponse<unknown>;

export type UpdateTreasuryReinvestDividendResponse = ApiResponse<{ reinvestDividend: boolean }>;

/**
 * The treasury portfolio, live from madkhol-portfolio's portfolio card calculation (the app's own card numbers).
 * Mirrors madkhol-admin ITreasuryPortfolioFigures.
 */
export interface TreasuryPortfolioFigures {
  id: number;
  nameEn: string;
  status: string | null;
  /** An order still in flight on it, if any. */
  currentStatus: string | null;
  /** Market value + the portfolio's cash sleeve. */
  overallBalance: number;
  returnSAR: number;
  returnPercentage: number;
  totalCostBase: number;
  /** Cost base + cash sleeve + in-flight subscription money. */
  totalInvestedAmount: number;
  cacheAllocationValue: number;
}

/**
 * The company's VIRTUAL_TREASURY bank (madkhol-admin ITreasuryBank). A withdraw request moves money from `amount` to
 * `hold`, so `amount` is already free to spend and the total is amount + hold.
 */
export interface TreasuryBank {
  id: number;
  amount: number;
  hold: number;
  iban: string | null;
  accountNumber: string | null;
}

/** madkhol-admin ITreasuryAnalysis — all live. */
export interface TreasuryAnalysis {
  /** null until the first executed contribution creates the portfolio. */
  portfolio: TreasuryPortfolioFigures | null;
  treasuryBankCash: number;
  treasuryBank: TreasuryBank;
}

export type FetchTreasuryAnalysisResponse = ApiResponse<TreasuryAnalysis>;
